import React, { useEffect, useState } from "react";
import SellerAuth from ".";
import { AUTH_FORM_SHELL_CLASS } from "./auth-shell";
import {
  Checkbox,
  Grid2,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
} from "@mui/material";
import MaInput from "../../Components/UI/ma-input";
import MaButton from "../../Components/UI/ma-button";
import { GET_COUNTRIES } from "../../Utils/Queries";
import {
  CREATE_PIN,
  RESEND_OTP,
  SIGNUP_MUTATION,
  VALIDATE_OTP,
} from "../../Utils/Mutations";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserEmail, setUserId } from "../../store/userSlice";
import { AnimatePresence, motion } from "framer-motion";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  IoArrowBack,
  IoMailOutline,
  IoLockClosedOutline,
} from "react-icons/io5";
import { IoIosArrowForward } from "react-icons/io";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { MuiOtpInput } from "mui-one-time-password-input";
import Timeout from "../../Pages/Auth/Timeout";
import useAuthFlow from "../../Hooks/use-auth-flow";

const normalizePhone = (phone) => (phone || "").replace(/[^\d+]/g, "");

const OTP_DURATION = 60;

// Explicit step ids — easier to read than magic numbers.
const SIGNUP_STEP = 3;
const VERIFY_STEP = 4;
const PIN_STEP = 5;

const STEPS = [
  {
    id: 1,
    label: "Personal information",
    heading: "Create your account",
    subheading: "Let's start with the basics about you.",
    progress: 20,
  },
  {
    id: 2,
    label: "Contact details",
    heading: "How can we reach you?",
    subheading: "Add contact info so buyers and Mamaket can reach you.",
    progress: 40,
  },
  {
    id: 3,
    label: "Account security",
    heading: "Secure your account",
    subheading: "Pick a strong password and review our terms.",
    progress: 60,
  },
  {
    id: 4,
    label: "Verify email",
    heading: "Verify your email",
    subheading: "Enter the 6-digit code we just sent to your inbox.",
    progress: 80,
  },
  {
    id: 5,
    label: "Create transaction PIN",
    heading: "Create transaction PIN",
    subheading: "Set a 4-digit PIN to authorize all your transactions.",
    progress: 100,
  },
];

const SCHEMAS = {
  1: yup.object({
    firstname: yup
      .string()
      .trim()
      .required("First name is required")
      .min(2, "Too short"),
    lastname: yup
      .string()
      .trim()
      .required("Last name is required")
      .min(2, "Too short"),
    email: yup
      .string()
      .trim()
      .email("Enter a valid email")
      .required("Email is required"),
  }),
  2: yup.object({
    phone: yup
      .string()
      .required("Phone number is required")
      .min(8, "Enter a valid phone number"),
    country: yup.string().required("Select your country"),
    referralCode: yup.string().nullable(),
  }),
  3: yup.object({
    password: yup
      .string()
      .required("Password is required")
      .min(8, "At least 8 characters")
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]+$/,
        "Use letters, a number and a special character",
      ),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], "Passwords do not match")
      .required("Confirm your password"),
    agreedToTerms: yup.boolean().oneOf([true], "You must agree to continue"),
  }),
  [PIN_STEP]: yup.object({
    pin: yup
      .string()
      .required("PIN is required")
      .matches(/^\d{4}$/, "PIN must be 4 digits"),
    confirmPin: yup
      .string()
      .required("Confirm your PIN")
      .oneOf([yup.ref("pin"), null], "PINs must match"),
  }),
};

const STEP_FIELDS = {
  1: ["firstname", "lastname", "email"],
  2: ["phone", "country", "referralCode"],
  3: ["password", "confirmPassword", "agreedToTerms"],
  [PIN_STEP]: ["pin", "confirmPin"],
};

const EASE = [0.22, 1, 0.36, 1];

const stepVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 32 : -32,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: EASE,
      when: "beforeChildren",
      staggerChildren: 0.07,
    },
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction > 0 ? -32 : 32,
    transition: { duration: 0.3, ease: EASE },
  }),
};

const fieldVariants = {
  enter: { opacity: 0, y: 14 },
  center: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: EASE },
  },
};

const SellerCreateAccount = () => {
  const { data: countryData } = useQuery(GET_COUNTRIES);
  const country_list = countryData?.getAllCountries?.items || [];

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAuth } = useAuthFlow();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // OTP state for step 4
  const [otpValue, setOtpValue] = useState("");
  const [otpValidate, setOtpValidate] = useState(true);
  const [signedUpUuid, setSignedUpUuid] = useState(null);
  const [timerKey, setTimerKey] = useState(0);

  const [signup, { loading: signUpLoad, error: signUpError }] =
    useMutation(SIGNUP_MUTATION);
  const [
    validateOtpMutation,
    { loading: validateLoading, error: validateError },
  ] = useMutation(VALIDATE_OTP);
  const [resendOtpMutation, { loading: resendLoading }] =
    useMutation(RESEND_OTP);
  const [createPinMutation, { loading: pinLoading, error: pinError }] =
    useMutation(CREATE_PIN);

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      country: "",
      referralCode: "",
      password: "",
      confirmPassword: "",
      agreedToTerms: false,
      pin: "",
      confirmPin: "",
      lat: 0,
      long: 0,
      favouriteCategories: [],
    },
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      const errors = {};
      const schema = SCHEMAS[step];
      if (!schema) return errors;
      try {
        schema.validateSync(values, { abortEarly: false });
      } catch (err) {
        if (err.inner) {
          err.inner.forEach((e) => {
            if (e.path && !errors[e.path]) errors[e.path] = e.message;
          });
        }
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const response = await signup({
          variables: {
            firstName: values.firstname.trim(),
            lastName: values.lastname.trim(),
            email: values.email.trim(),
            phone: normalizePhone(values.phone),
            password: values.password,
            lat: values.lat || 0,
            long: values.long || 0,
            country: values.country,
            favouriteCategories: values.favouriteCategories || [],
            referralCode: values.referralCode || "",
          },
        });

        const uuid = response?.data?.signUp?.uuid || response?.data?.signUp;
        if (uuid) {
          setSignedUpUuid(uuid);
          dispatch(setUserId(uuid));
        }
        dispatch(setUserEmail(values.email.trim()));

        // Move into the email verification step instead of navigating away.
        setOtpValue("");
        setOtpValidate(true);
        setTimerKey((k) => k + 1);
        setDirection(1);
        setStep(VERIFY_STEP);
      } catch (err) {
        // Apollo surfaces the error via signUpError; keep onSubmit resilient.
      }
    },
  });

  useEffect(() => {
    if (!navigator?.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        formik.setFieldValue("lat", position.coords.latitude);
        formik.setFieldValue("long", position.coords.longitude);
      },
      () => {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run validation when the step changes so errors reflect the active step.
  useEffect(() => {
    if (SCHEMAS[step]) formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const currentStep = STEPS[step - 1];

  const showError = (field) =>
    Boolean(formik.touched[field] && formik.errors[field]);

  const helper = (field) => (showError(field) ? formik.errors[field] : "");

  const handleResendOtp = async () => {
    try {
      await resendOtpMutation({
        variables: { email: formik.values.email.trim() },
      });
      setOtpValidate(true);
      setTimerKey((k) => k + 1);
    } catch (err) {
      // Surface error via UI banner if needed.
    }
  };

  const handleValidateOtp = async (codeOverride) => {
    const code = codeOverride || otpValue;
    if (!signedUpUuid || code.length < 6) return;
    try {
      const { data } = await validateOtpMutation({
        variables: { uuid: signedUpUuid, otp: code },
      });
      const verifiedUserId = data?.validateOTP?.userId;
      if (verifiedUserId) {
        dispatch(setUserId(verifiedUserId));
        setSignedUpUuid(verifiedUserId);
      }

      // Advance into the create PIN step instead of navigating away.
      setDirection(1);
      setStep(PIN_STEP);
    } catch (err) {
      // validateError covers UI state.
    }
  };

  const handleCreatePin = async () => {
    if (!signedUpUuid) return;
    try {
      await createPinMutation({
        variables: {
          userId: signedUpUuid,
          transactionPin: formik.values.pin,
        },
      });

      // Mirror the existing /createpin behavior: send user to login.
      navigate("/");
      triggerAuth("signin");
    } catch (err) {
      // pinError covers UI state.
    }
  };

  const validateStepFields = async (fields) => {
    const touchedObj = {};
    fields.forEach((f) => {
      touchedObj[f] = true;
    });
    formik.setTouched({ ...formik.touched, ...touchedObj }, false);
    const errors = await formik.validateForm();
    return fields.every((f) => !errors[f]);
  };

  const validateAndProceed = async () => {
    if (step === VERIFY_STEP) return handleValidateOtp();

    if (step === PIN_STEP) {
      const ok = await validateStepFields(STEP_FIELDS[PIN_STEP]);
      if (!ok) return;
      return handleCreatePin();
    }

    const ok = await validateStepFields(STEP_FIELDS[step]);
    if (!ok) return;

    if (step < SIGNUP_STEP) {
      setDirection(1);
      setStep(step + 1);
    } else if (step === SIGNUP_STEP) {
      // Triggers signup; on success the onSubmit handler advances to VERIFY_STEP.
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    // Only allow going back within the form-collection phase (steps 1–3).
    if (step > 1 && step <= SIGNUP_STEP) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const ctaTitle =
    step === PIN_STEP
      ? "Create PIN"
      : step === VERIFY_STEP
        ? "Verify email"
        : step === SIGNUP_STEP
          ? "Create account"
          : "Continue";

  const ctaLoading = signUpLoad || validateLoading || pinLoading;

  const ctaDisabled =
    ctaLoading ||
    (step === VERIFY_STEP && otpValue.length < 6) ||
    (step === PIN_STEP &&
      (formik.values.pin.length < 4 || formik.values.confirmPin.length < 4));

  const showBack = step > 1 && step <= SIGNUP_STEP;

  return (
    <SellerAuth>
      <div className={AUTH_FORM_SHELL_CLASS}>
        <div>
          <span className="text-[#CC9901] font-bold text-[13px] sm:text-[14px]">
            Step {step} of {STEPS.length}: {currentStep.label}
          </span>
          <LinearProgress
            variant="determinate"
            value={currentStep.progress}
            sx={{
              borderRadius: "50px",
              mt: 1,
              height: 5,
              backgroundColor: "#F5E6B8",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#CC9901",
                borderRadius: "50px",
                transition: "transform 0.7s cubic-bezier(0.22,1,0.36,1)",
              },
            }}
          />
        </div>

        <div className="mt-6">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={step}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <motion.h2
                variants={fieldVariants}
                className="my-0 text-[#353535] text-[22px] sm:text-[26px] md:text-[28px] font-bold"
              >
                {currentStep.heading}
              </motion.h2>
              <motion.span
                variants={fieldVariants}
                className="block mt-2 text-[13px] sm:text-[14px] text-[#222222]"
              >
                {currentStep.subheading}
              </motion.span>

              <div className="mt-6">
                <Grid2 container spacing={2}>
                  {step === 1 && (
                    <>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="firstname"
                            name="firstname"
                            label="First name"
                            placeholder="Enter first name"
                            fullWidth
                            value={formik.values.firstname}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("firstname")}
                            helperText={helper("firstname")}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="lastname"
                            name="lastname"
                            label="Last name"
                            placeholder="Enter last name"
                            fullWidth
                            value={formik.values.lastname}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("lastname")}
                            helperText={helper("lastname")}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="email"
                            name="email"
                            label="Email address"
                            placeholder="you@example.com"
                            fullWidth
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("email")}
                            helperText={helper("email")}
                          />
                        </motion.div>
                      </Grid2>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            phone
                            label="Phone number"
                            fullWidth
                            value={formik.values.phone}
                            onChange={(value) =>
                              formik.setFieldValue("phone", value)
                            }
                            onBlur={() => formik.setFieldTouched("phone", true)}
                            error={showError("phone")}
                            helperText={helper("phone")}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            select
                            label="Country"
                            fullWidth
                            value={formik.values.country}
                            onChange={(e) =>
                              formik.setFieldValue("country", e.target.value)
                            }
                            onBlur={() =>
                              formik.setFieldTouched("country", true)
                            }
                            error={showError("country")}
                            helperText={helper("country")}
                          >
                            <MenuItem value="" disabled>
                              Select country
                            </MenuItem>
                            {country_list?.map((c) => (
                              <MenuItem key={c?._id} value={c?._id}>
                                {c?.name}
                              </MenuItem>
                            ))}
                          </MaInput>
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="referralCode"
                            name="referralCode"
                            label="Referral code (optional)"
                            placeholder="XXXX-XXXX"
                            fullWidth
                            value={formik.values.referralCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </motion.div>
                      </Grid2>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="password"
                            name="password"
                            label="Password"
                            placeholder="Create a strong password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("password")}
                            helperText={helper("password")}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => setShowPassword((s) => !s)}
                                    edge="end"
                                  >
                                    {showPassword ? (
                                      <VisibilityOutlinedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    ) : (
                                      <VisibilityOffOutlinedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="confirmPassword"
                            name="confirmPassword"
                            label="Confirm password"
                            placeholder="Re-enter your password"
                            type={showConfirm ? "text" : "password"}
                            fullWidth
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("confirmPassword")}
                            helperText={helper("confirmPassword")}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => setShowConfirm((s) => !s)}
                                    edge="end"
                                  >
                                    {showConfirm ? (
                                      <VisibilityOutlinedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    ) : (
                                      <VisibilityOffOutlinedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <label className="flex items-center gap-x-2 cursor-pointer select-none">
                            <Checkbox
                              checked={formik.values.agreedToTerms}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "agreedToTerms",
                                  e.target.checked,
                                )
                              }
                              onBlur={() =>
                                formik.setFieldTouched("agreedToTerms", true)
                              }
                              sx={{
                                p: 0,
                                mt: "2px",
                                color: "#CC9901",
                                "&.Mui-checked": { color: "#CC9901" },
                              }}
                            />
                            <span className="text-[13px] text-[#353535] leading-snug">
                              I agree to Mamaket's{" "}
                              <Link to="" className="underline">
                                Terms of Service
                              </Link>{" "}
                              and{" "}
                              <Link to="" className="underline">
                                Privacy Policy
                              </Link>
                              .
                            </span>
                          </label>
                          {showError("agreedToTerms") && (
                            <p className="text-[12px] text-[#d32f2f] mt-1 ml-7 mb-0">
                              {formik.errors.agreedToTerms}
                            </p>
                          )}
                        </motion.div>
                      </Grid2>
                    </>
                  )}

                  {step === VERIFY_STEP && (
                    <Grid2 size={{ xs: 12 }}>
                      <motion.div variants={fieldVariants}>
                        <div className="mb-4 flex flex-col gap-2 rounded-[10px] border border-solid border-[#F5E6B8] bg-[#FFF8E1] px-3 py-2 sm:flex-row sm:items-center sm:gap-x-2">
                          <IoMailOutline className="shrink-0 text-[18px] text-[#CC9901]" />
                          <p className="my-0 text-[12px] leading-snug text-[#5C4A00] sm:text-[13px]">
                            We sent a code to{" "}
                            <span className="font-semibold text-[#353535]">
                              {formik.values.email || "your email"}
                            </span>
                          </p>
                        </div>
                      </motion.div>

                      <motion.div variants={fieldVariants}>
                        <MuiOtpInput
                          value={otpValue}
                          onChange={setOtpValue}
                          length={6}
                          autoFocus
                          TextFieldsProps={{
                            placeholder: "•",
                            inputMode: "numeric",
                          }}
                          onComplete={(code) => handleValidateOtp(code)}
                          sx={{
                            width: "100%",
                            justifyContent: "space-between",
                            gap: { xs: 0.75, sm: 1.5 },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              fontWeight: 600,
                              fontSize: { xs: 15, sm: 18 },
                              flex: { xs: "1 1 0", sm: "0 0 auto" },
                              minWidth: 0,
                              maxWidth: { xs: 44, sm: 56 },
                              "& fieldset": { borderColor: "#DADADA" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#CC9901",
                                borderWidth: "1.5px",
                              },
                            },
                            "& input": {
                              height: { xs: 36, sm: 44 },
                              padding: 0,
                            },
                          }}
                        />
                      </motion.div>

                      <motion.div
                        variants={fieldVariants}
                        className="mt-4 flex flex-wrap items-center justify-center gap-x-1 text-center text-[12px] text-[#666] sm:text-[13px]"
                      >
                        {otpValidate ? (
                          <>
                            <span>Didn't receive the code? Resend in</span>
                            <Timeout
                              key={timerKey}
                              duration={OTP_DURATION}
                              onTimeout={() => setOtpValidate(false)}
                            />
                          </>
                        ) : (
                          <>
                            <span>Didn't receive the code?</span>
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              disabled={resendLoading}
                              className="ml-2 font-semibold text-[#2E1834] cursor-pointer bg-transparent border-0 underline disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {resendLoading ? "Resending..." : "Resend"}
                            </button>
                          </>
                        )}
                      </motion.div>
                    </Grid2>
                  )}

                  {step === PIN_STEP && (
                    <Grid2 size={{ xs: 12 }}>
                      <motion.div variants={fieldVariants}>
                        <div className="mb-5 flex flex-col gap-2 rounded-[10px] border border-solid border-[#D7B9DF] bg-[#F5EEF7] px-3 py-2 sm:flex-row sm:items-center sm:gap-x-2">
                          <IoLockClosedOutline className="shrink-0 text-[18px] text-[#2E1834]" />
                          <p className="my-0 text-[12px] leading-snug text-[#2E1834] sm:text-[13px]">
                            You'll use this 4-digit PIN to authorize
                            transactions on your account.
                          </p>
                        </div>
                      </motion.div>

                      <motion.div variants={fieldVariants} className="mb-4">
                        <InputLabel
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            mb: 1,
                            fontFamily: "lato",
                            color: "#272727",
                          }}
                        >
                          Enter 4-digit PIN
                        </InputLabel>
                        <MuiOtpInput
                          value={formik.values.pin}
                          onChange={(val) =>
                            formik.setFieldValue("pin", val.replace(/\D/g, ""))
                          }
                          length={4}
                          autoFocus
                          TextFieldsProps={{
                            type: "password",
                            placeholder: "•",
                            inputMode: "numeric",
                          }}
                          sx={{
                            justifyContent: "flex-start",
                            gap: { xs: 1, sm: 1.5 },
                            maxWidth: { xs: "100%", sm: 320 },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              fontWeight: 600,
                              fontSize: { xs: 16, sm: 18 },
                              "& fieldset": {
                                borderColor: showError("pin")
                                  ? "#d32f2f"
                                  : "#DADADA",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: showError("pin")
                                  ? "#d32f2f"
                                  : "#CC9901",
                                borderWidth: "1.5px",
                              },
                            },
                            "& input": {
                              height: { xs: 40, sm: 44 },
                              padding: 0,
                            },
                          }}
                        />
                        {showError("pin") && (
                          <p className="text-[12px] text-[#d32f2f] mt-1 mb-0">
                            {formik.errors.pin}
                          </p>
                        )}
                      </motion.div>

                      <motion.div variants={fieldVariants}>
                        <InputLabel
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            mb: 1,
                            fontFamily: "lato",
                            color: "#272727",
                          }}
                        >
                          Confirm 4-digit PIN
                        </InputLabel>
                        <MuiOtpInput
                          value={formik.values.confirmPin}
                          onChange={(val) =>
                            formik.setFieldValue(
                              "confirmPin",
                              val.replace(/\D/g, ""),
                            )
                          }
                          length={4}
                          TextFieldsProps={{
                            type: "password",
                            placeholder: "•",
                            inputMode: "numeric",
                          }}
                          sx={{
                            justifyContent: "flex-start",
                            gap: { xs: 1, sm: 1.5 },
                            maxWidth: { xs: "100%", sm: 320 },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              fontWeight: 600,
                              fontSize: { xs: 16, sm: 18 },
                              "& fieldset": {
                                borderColor: showError("confirmPin")
                                  ? "#d32f2f"
                                  : "#DADADA",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: showError("confirmPin")
                                  ? "#d32f2f"
                                  : "#CC9901",
                                borderWidth: "1.5px",
                              },
                            },
                            "& input": {
                              height: { xs: 40, sm: 44 },
                              padding: 0,
                            },
                          }}
                        />
                        {showError("confirmPin") && (
                          <p className="text-[12px] text-[#d32f2f] mt-1 mb-0">
                            {formik.errors.confirmPin}
                          </p>
                        )}
                      </motion.div>
                    </Grid2>
                  )}
                </Grid2>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {((step === SIGNUP_STEP && signUpError) ||
            (step === VERIFY_STEP && validateError) ||
            (step === PIN_STEP && pinError)) && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="mt-5 flex items-center justify-center"
            >
              <span className="text-[#FF465C] text-[13px] font-medium border border-solid w-fit bg-[#FF465C1a] rounded-full border-[#FF465C] px-3 py-1 text-center">
                {step === VERIFY_STEP && validateError
                  ? validateError.message ===
                    "Cannot read properties of null (reading 'toString')"
                    ? "Wrong OTP details"
                    : validateError.message
                  : step === PIN_STEP && pinError
                    ? pinError.message ===
                      "Profile already completed. Kindly login instead."
                      ? "Profile already completed. Kindly login instead."
                      : pinError.message
                    : signUpError?.message}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          layout
          transition={{ duration: 0.4, ease: EASE }}
          className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:items-center"
        >
          {showBack && (
            <div className="w-full sm:w-auto">
              <MaButton
                onClick={handleBack}
                disabled={signUpLoad}
                fullWidth
                startIcon={<IoArrowBack />}
                title="Back"
                sx={{
                  bgcolor: "transparent",
                  border: "1px solid #2E1834",
                  color: "#2E1834",
                  boxShadow: "none",
                  minWidth: { xs: "100%", sm: 140 },
                  borderRadius: "12px",
                }}
              />
            </div>
          )}
          <div className="w-full sm:flex-1">
            <MaButton
              onClick={validateAndProceed}
              variant="primary"
              fullWidth
              loading={ctaLoading}
              disabled={ctaDisabled}
              title={ctaTitle}
              endIcon={
                step < SIGNUP_STEP && !ctaLoading ? <IoIosArrowForward /> : null
              }
              sx={{
                minWidth: 0,
                bgcolor: "#2E1834",
                borderRadius: "12px",
              }}
            />
          </div>
        </motion.div>
      </div>
    </SellerAuth>
  );
};

export default SellerCreateAccount;
